import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Immutable from 'immutable'
import { useDispatch, useSelector } from 'react-redux'

import {
  buildUpsellsContentBlockViewModel,
  type BasketUpsellProduct,
  type BasketUpsellProductActionSource,
} from '@features/scrollable-basket'
import type { BasketContentBlock, UpsellsContentBlock } from '@library/api-menu-experience'
import { queueTrackingEvent } from '@library/tracking-integration'

import { actionTypes } from 'actions/actionTypes'
import { getBasketProducts } from 'selectors/basket'
import { getBasket } from 'selectors/root'

import { useBasketProductErrorHandling } from './useBasketProductErrorHandling'
import { useFetchBasketContent } from './useFetchBasketContent'

type BasketRecipe = {
  id: string
  quantity: number
  numPortions: number
}

type UseBasketContentInput = {
  basketDate?: string
  basketRecipes: BasketRecipe[]
  deliverySlotId?: string
  menuUuid?: string | null
  numPortions: number
  orderId?: string
  postcode?: string
  shippingAddressId?: string
}

type DisplayRthCarouselEvent = {
  action: 'display_rth_carousel'
  properties: {
    menuUuid?: string
    productsShown: string[]
  }
}

const isUpsellsContentBlock = (block: BasketContentBlock): block is UpsellsContentBlock =>
  block.type === 'upsells' && 'products' in block

const canAddProduct = (product?: BasketUpsellProduct) =>
  Boolean(product?.isAvailable && !product.outOfStock && !product.limitReached)

const canRemoveProduct = (product?: BasketUpsellProduct) => Boolean(product && product.qty > 0)

export const useBasketContent = ({
  basketDate,
  basketRecipes,
  deliverySlotId,
  menuUuid,
  numPortions,
  orderId,
  postcode,
  shippingAddressId,
}: UseBasketContentInput) => {
  const dispatch = useDispatch()
  const basket = useSelector(getBasket)
  const basketProducts =
    useSelector((state) => getBasketProducts(state as any)) ?? Immutable.Map<string, number>()
  const [productDetailsId, setProductDetailsId] = useState<string | null>(null)
  const hasTrackedCarouselDisplay = useRef(false)

  const basketProductQuantities = useMemo(
    () => basketProducts.toJS() as Record<string, number>,
    [basketProducts],
  )
  const basketGifts = useMemo(
    () => (basket.get('gifts')?.toJS?.() ?? {}) as Record<string, number>,
    [basket],
  )
  const basketContent = useFetchBasketContent({
    basketDate,
    basketGifts,
    basketProductQuantities,
    basketRecipes,
    deliverySlotId,
    menuUuid,
    numPortions,
    orderId,
    postcode,
    shippingAddressId,
  })

  const groupedProducts = useMemo(() => {
    const products = new Map<string, BasketUpsellProduct>()

    basketContent.content?.filter(isUpsellsContentBlock).forEach((block) => {
      const viewModel = buildUpsellsContentBlockViewModel({
        basketGifts,
        basketProductQuantities,
        block,
      })

      ;[...viewModel.carouselProducts, ...viewModel.selectedProducts].forEach((product) => {
        products.set(product.id, product)
      })
    })

    return products
  }, [basketContent.content, basketGifts, basketProductQuantities])

  const carouselProductIds = useMemo(() => {
    const block = basketContent.content
      ?.filter(isUpsellsContentBlock)
      .find((contentBlock) => (contentBlock.carousel?.productIds.length ?? 0) >= 2)

    if (!block) return []

    return buildUpsellsContentBlockViewModel({
      basketGifts,
      basketProductQuantities,
      block,
    }).carouselProducts.map(({ id }) => id)
  }, [basketContent.content, basketGifts, basketProductQuantities])

  useEffect(() => {
    if (hasTrackedCarouselDisplay.current || carouselProductIds.length < 2) {
      return
    }

    queueTrackingEvent<DisplayRthCarouselEvent>({
      action: 'display_rth_carousel',
      properties: { menuUuid: menuUuid ?? undefined, productsShown: carouselProductIds },
    })
    hasTrackedCarouselDisplay.current = true
  }, [carouselProductIds, menuUuid])

  const dispatchAddProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      dispatch({
        type: actionTypes.BASKET_PRODUCT_ADD,
        productId,
        unsaved: true,
        trackingData: {
          actionType: actionTypes.BASKET_PRODUCT_ADD,
          menuUuid,
          productId,
          source,
        },
      })
    },
    [dispatch, menuUuid],
  )

  const dispatchRemoveProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      dispatch({
        type: actionTypes.BASKET_PRODUCT_REMOVE,
        productId,
        unsaved: true,
        trackingData: {
          actionType: actionTypes.BASKET_PRODUCT_REMOVE,
          menuUuid,
          productId,
          source,
        },
      })
    },
    [dispatch, menuUuid],
  )

  const addProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      if (!canAddProduct(groupedProducts.get(productId))) return

      dispatchAddProduct(productId, source)
    },
    [dispatchAddProduct, groupedProducts],
  )

  const removeProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      if (!canRemoveProduct(groupedProducts.get(productId))) return

      dispatchRemoveProduct(productId, source)
    },
    [dispatchRemoveProduct, groupedProducts],
  )

  const {
    addProduct: addProductWithErrorHandling,
    dismissProductError,
    pendingProductIds,
    productError,
    removeProduct: removeProductWithErrorHandling,
  } = useBasketProductErrorHandling({
    addProduct: dispatchAddProduct,
    basketError: basketContent.error,
    isBasketValidating: basketContent.isValidating,
    removeProduct: dispatchRemoveProduct,
  })

  const addBasketProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      const product = groupedProducts.get(productId)
      if (!canAddProduct(product) || pendingProductIds.has(productId)) {
        return
      }

      addProductWithErrorHandling(productId, source)
    },
    [addProductWithErrorHandling, groupedProducts, pendingProductIds],
  )

  const removeBasketProduct = useCallback(
    (productId: string, source: BasketUpsellProductActionSource) => {
      const product = groupedProducts.get(productId)
      if (!canRemoveProduct(product) || pendingProductIds.has(productId)) return

      removeProductWithErrorHandling(productId, source)
    },
    [groupedProducts, pendingProductIds, removeProductWithErrorHandling],
  )

  const productDetail = productDetailsId ? groupedProducts.get(productDetailsId) ?? null : null

  return {
    dismissProductError,
    productError,
    basketContent: {
      ...basketContent,
      basketGifts,
      basketProductQuantities,
      basketRecipes,
      pendingProductIds,
      onAddProduct: addBasketProduct,
      onClickProduct: (product: BasketUpsellProduct) => setProductDetailsId(product.id),
      onRemoveProduct: removeBasketProduct,
    },
    productDetail: productDetail
      ? {
          isAvailable: productDetail.isAvailable,
          limitReached: productDetail.limitReached ?? false,
          onAdd: (productId: string) => addProduct(productId, 'product_detail_modal'),
          onClose: () => setProductDetailsId(null),
          onRemove: (productId: string) => removeProduct(productId, 'product_detail_modal'),
          outOfStock: productDetail.stock <= 0,
          product: productDetail,
          qty: productDetail.qty,
        }
      : null,
  }
}
